import { useMutation } from "@tanstack/react-query";
import { backendService } from "../services/backend.service";
import {
	ChangesSource,
	type DeleteShiftData,
	type PatchShiftData,
	type PostShiftData,
	type PostShiftRequestBody,
	type PostShiftRequestParams,
	ShiftType,
} from "../utils/models";
import { isOpenShiftBelowMaximumDuration } from "../utils/openShiftDuration";

export const useShifts = () => {
	const {
		mutateAsync: postShift,
		isPending: isPostingShift,
		error: postShiftError,
	} = useMutation({
		mutationFn: async ({
			roles,
			jobId,
			neededNurses,
			shiftNo,
			startTime,
			endTime,
			departmentId,
			bonus,
			isMasterSchedule,
			chargeOnly,
			location,
			agencyShiftIds,
			postToAgency,
			agencyLicenseTypes,
			agencyFacilityIds,
			agencySource,
			agencySourceName,
			source,
			subRole,
			postWithDelay,
			agencyPostDelayHours,
			incentiveConfigurationId,
		}: PostShiftData & { isMasterSchedule?: boolean }) => {
			if (!isOpenShiftBelowMaximumDuration(startTime, endTime)) {
				throw new Error("error.open.shift.duration.too.long");
			}
			const body: PostShiftRequestBody = {
				roles,
				job_id: jobId,
				needed_nurses: neededNurses,
				shift_type: shiftNo === 0 ? ShiftType.Day : ShiftType.Night,
				shift_start: startTime,
				shift_end: endTime,
				bonus,
				charge_only: chargeOnly,
				location,
				agency_shift_ids: agencyShiftIds,
				post_to_agency: postToAgency,
				agency_license_types: agencyLicenseTypes,
				agency_facility_ids: agencyFacilityIds,
				agency_source: agencySource,
				agency_source_name: agencySourceName,
				source: source,
				sub_role: subRole,
				post_with_delay: postWithDelay,
				agency_post_delay_hours: agencyPostDelayHours,
				incentive_configuration_id: incentiveConfigurationId,
			};

			const params: PostShiftRequestParams = {
				department_id: departmentId,
				source: isMasterSchedule ? ChangesSource.MasterSchedule : ChangesSource.Staffing,
			};
			const response = await backendService.postShift(body, params);

			return response.data;
		},
		throwOnError: true,
	});

	const {
		mutateAsync: patchShift,
		isSuccess: isPatchShiftSuccess,
		isPending: isPatchingShift,
		error: patchShiftError,
	} = useMutation({
		mutationFn: async ({ neededNurses, shiftId }: PatchShiftData) => {
			const response = await backendService.patchShift(shiftId, neededNurses);
			return response.data;
		},
	});

	const {
		mutateAsync: deleteShift,
		isPending: isDeletingShift,
		error: deleteShiftError,
	} = useMutation({
		mutationFn: async ({ shiftId, departmentId }: DeleteShiftData) => {
			const response = await backendService.deleteShift(shiftId, departmentId);
			return response.data;
		},
	});

	return {
		postShift,
		patchShift,
		deleteShift,
		isPatchShiftSuccess,
		isLoading: {
			postShift: isPostingShift,
			patchShift: isPatchingShift,
			deleteShift: isDeletingShift,
		},
		error: {
			postShift: postShiftError,
			patchShift: patchShiftError,
			deleteShift: deleteShiftError,
		},
	};
};
